/**
 * RBAC (Role-Based Access Control) Type Definitions for Frontend
 * Mirrors backend types for type safety
 */

// =============================================================================
// APPLICATIONS
// =============================================================================

export enum AppType {
    FIDELITY = 'fidelity',
    SAV = 'sav',
}

// =============================================================================
// ROLES
// =============================================================================

/**
 * User roles in the system (hierarchical)
 *
 * SUPER_ADMIN: Full access to everything, can manage dashboard users
 * MANAGER: Can view and perform actions (confirm claims, add influencers, approve agents, etc.)
 * VIEWER: Read-only access (can generate reports)
 */
export enum UserRole {
    SUPER_ADMIN = 'super_admin',
    MANAGER = 'manager',
    VIEWER = 'viewer',
}

/**
 * Role hierarchy for comparison
 * Higher number = more permissions
 */
export const ROLE_HIERARCHY: Record<UserRole, number> = {
    [UserRole.SUPER_ADMIN]: 100,
    [UserRole.MANAGER]: 50,
    [UserRole.VIEWER]: 25,
};

// =============================================================================
// USER INTERFACE
// =============================================================================

export interface AuthUser {
    uid: string;
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
    role: UserRole;
    apps: AppType[];
    allowedBrands?: string[]; // Brand IDs the user can access (undefined or empty = all brands)
    wilayas?: string[]; // Wilaya (state) ids the user can access (undefined or empty = all wilayas)
}

export interface LoginCredentials {
    email: string;
    password: string;
}

export interface LoginResponse {
    success: boolean;
    data?: {
        user: AuthUser;
        token: string;
        expiresIn: string;
    };
    error?: {
        code: string;
        message: string;
    };
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getRoleDisplayName(role: UserRole): string {
    const names: Record<UserRole, string> = {
        [UserRole.SUPER_ADMIN]: 'Super Administrateur',
        [UserRole.MANAGER]: 'Manager',
        [UserRole.VIEWER]: 'Lecteur',
    };
    return names[role] || role;
}

export function getAppDisplayName(app: AppType): string {
    const names: Record<AppType, string> = {
        [AppType.FIDELITY]: 'Fidelity System',
        [AppType.SAV]: 'SAV Reports',
    };
    return names[app] || app;
}

export function canAccessApp(user: AuthUser | null, app: AppType): boolean {
    if (!user) return false;
    if (user.role === UserRole.SUPER_ADMIN) return true;
    return user.apps.includes(app);
}

export function hasMinRole(user: AuthUser | null, minRole: UserRole): boolean {
    if (!user) return false;
    return ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY[minRole];
}

export function isViewer(user: AuthUser | null): boolean {
    return hasMinRole(user, UserRole.VIEWER);
}

export function isManager(user: AuthUser | null): boolean {
    return hasMinRole(user, UserRole.MANAGER);
}

export function isSuperAdmin(user: AuthUser | null): boolean {
    return user?.role === UserRole.SUPER_ADMIN;
}
